import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi, analyticsApi, formatApiErrorDetail, useAuth } from '../../lib/api';
import { formatCurrency, getAttachRateColor, getAttachRateBg } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Store,
  ShieldCheck,
  TrendingUp,
  Receipt,
  ArrowRight,
  Loader2,
  Users,
  Award,
  Clock,
  ShoppingCart,
} from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

function TrendsChart({ data }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
        <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#64748b' }} />
        <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
        <Tooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13 }} />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        <Bar dataKey="total_sales" name="Total Sales" fill="#94a3b8" radius={[4,4,0,0]} />
        <Bar dataKey="warranty_sales" name="Warranty Sales" fill="#005DE8" radius={[4,4,0,0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function ShopOwnerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashRes, trendsRes] = await Promise.all([
          dashboardApi.shopOwner(),
          analyticsApi.trends(6),
        ]);
        setData(dashRes.data);
        setTrends(trendsRes.data);
      } catch (error) {
        toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Welcome back, {user?.name?.split(' ')[0]}!
          </h1>
          <p className="text-slate-600 mt-1">
            {user?.company_name ? `${user.company_name} — ` : ''}Here's how your business is performing this month
          </p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Total Sales */}
          <Card className="border-slate-200 animate-slide-up stagger-1" data-testid="kpi-total-sales">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Total Sales</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Outfit' }}>
                    {data?.total_sales || 0}
                  </p>
                  <p className="text-sm text-slate-500 mt-2">This month</p>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                  <Store className="h-6 w-6 text-[#005DE8]" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Attach Rate */}
          <Card className="border-slate-200 animate-slide-up stagger-2" data-testid="kpi-attach-rate">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Attach Rate</p>
                  <p className={`text-3xl font-bold mt-2 ${getAttachRateColor(data?.attach_rate || 0)}`} style={{ fontFamily: 'Outfit' }}>
                    {data?.attach_rate || 0}%
                  </p>
                  <div className="mt-3">
                    <Progress 
                      value={data?.attach_rate || 0} 
                      className="h-2"
                    />
                  </div>
                </div>
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Warranty Revenue */}
          <Card className="border-slate-200 animate-slide-up stagger-3" data-testid="kpi-warranty-revenue">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Warranty Revenue</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Outfit' }}>
                    {formatCurrency(data?.warranty_revenue || 0)}
                  </p>
                  <p className="text-sm text-slate-500 mt-2">This month</p>
                </div>
                <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                  <ShieldCheck className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Outstanding Dues */}
          <Card className="border-slate-200 animate-slide-up stagger-4" data-testid="kpi-outstanding">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Outstanding</p>
                  <p className={`text-3xl font-bold mt-2 ${(data?.outstanding_amount || 0) > 0 ? 'text-red-600' : 'text-emerald-600'}`} style={{ fontFamily: 'Outfit' }}>
                    {formatCurrency(data?.outstanding_amount || 0)}
                  </p>
                  <p className="text-sm text-slate-500 mt-2">Invoice dues</p>
                </div>
                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                  <Receipt className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Warranty Profit */}
          <Card className="border-slate-200 animate-slide-up stagger-4" data-testid="kpi-warranty-profit">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Warranty Profit</p>
                  <p className="text-3xl font-bold text-emerald-600 mt-2" style={{ fontFamily: 'Outfit' }}>
                    {formatCurrency(data?.warranty_profit || 0)}
                  </p>
                  <p className="text-sm text-slate-500 mt-2">This month</p>
                </div>
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <Award className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Approvals Banner */}
        {(data?.pending_approvals || 0) > 0 && (
          <Card className="border-amber-200 bg-amber-50" data-testid="pending-approvals-banner">
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                    <Clock className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-amber-900">
                      {data.pending_approvals} sale{data.pending_approvals !== 1 ? 's' : ''} pending your approval
                    </p>
                    <p className="text-sm text-amber-700">Staff-submitted sales are waiting for review</p>
                  </div>
                </div>
                <Link to="/dashboard/sales">
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="review-approvals-btn">
                    Review Now
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trends Chart */}
        {trends.length > 0 && (
          <Card className="border-slate-200" data-testid="trends-chart">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Monthly Trends</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <TrendsChart data={trends} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Branch Performance & Top Staff */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Branch Performance */}
          <Card className="border-slate-200" data-testid="branch-performance-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                  Branch Performance
                </CardTitle>
                <Link to="/dashboard/branches">
                  <Button variant="ghost" size="sm" className="text-[#005DE8]">
                    View All
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {data?.branch_performance?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-100">
                      <TableHead>Branch</TableHead>
                      <TableHead className="text-right">Sales</TableHead>
                      <TableHead className="text-right">Warranties</TableHead>
                      <TableHead className="text-right">Rate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.branch_performance.map((branch) => (
                      <TableRow key={branch.branch_id} className="table-row-hover">
                        <TableCell className="font-medium">{branch.branch_name}</TableCell>
                        <TableCell className="text-right">{branch.sales_count}</TableCell>
                        <TableCell className="text-right">{branch.warranty_count}</TableCell>
                        <TableCell className={`text-right font-medium ${getAttachRateColor(branch.attach_rate)}`}>
                          {branch.attach_rate}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Store className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  No branch data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Top Performing Staff */}
          <Card className="border-slate-200" data-testid="top-staff-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                  Top Performing Staff
                </CardTitle>
                <Link to="/dashboard/incentives">
                  <Button variant="ghost" size="sm" className="text-[#005DE8]">
                    View All
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardHeader>
            <CardContent>
              {data?.top_staff?.length > 0 ? (
                <div className="space-y-3">
                  {data.top_staff.map((staff, index) => (
                    <div
                      key={staff.user_id}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#005DE8] flex items-center justify-center text-white font-medium text-sm">
                          {index + 1}
                        </div>
                        <span className="font-medium text-slate-900">{staff.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Award className="h-4 w-4 text-amber-500" />
                        <span className="font-semibold">{staff.warranties_sold}</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Users className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                  No staff data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Link to="/dashboard/new-sale">
                <Button className="bg-[#005DE8] hover:bg-[#0047B3]" data-testid="action-new-sale">
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  Book a Sale
                </Button>
              </Link>
              <Link to="/dashboard/branches">
                <Button variant="outline" data-testid="action-manage-branches">
                  <Store className="h-4 w-4 mr-2" />
                  Manage Branches
                </Button>
              </Link>
              <Link to="/dashboard/staff">
                <Button variant="outline" data-testid="action-manage-staff">
                  <Users className="h-4 w-4 mr-2" />
                  Manage Staff
                </Button>
              </Link>
              <Link to="/dashboard/invoices">
                <Button variant="outline" data-testid="action-view-invoices">
                  <Receipt className="h-4 w-4 mr-2" />
                  View Invoices
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
